import React, { useEffect, useState } from "react";
import axios from "axios";
import { GraduationCap, Clock, BookOpen, ShieldCheck, Loader2, Award, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const brl = (c) => (c / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function CoursesPublic() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", cpf: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/public/courses`).then(({ data }) => setCourses(data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const buy = async () => {
    if (!form.name || !form.email) { toast.error("Informe nome e e-mail"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/checkout`, { course_id: sel.id, name: form.name, email: form.email, cpf: form.cpf, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar o pagamento"); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#081325] text-white" data-testid="courses-public">
      <header className="flex items-center justify-between border-b border-white/10 bg-[#0A1C33] px-5 py-4">
        <div className="flex items-center gap-3">
          <img src="/valbrac-logo-3d.png" alt="Valbrac" className="h-10 w-10" />
          <div><p className="text-sm font-extrabold tracking-tight">VALBRAC · Escola Online</p><p className="text-[10px] uppercase tracking-wider text-emerald-400">Cursos de NRs · Certificado incluso</p></div>
        </div>
        <a href="/escola" className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-slate-200 hover:bg-white/10">Já sou aluno →</a>
      </header>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="mb-10 max-w-2xl">
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Capacitação em Normas Regulamentadoras</h1>
          <p className="mt-3 text-slate-400">Cursos online com carga horária de 8 horas, prova final e certificado profissional com conteúdo programático e assinatura técnica. Estude pelo computador ou celular.</p>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Certificado com QR e conteúdo programático</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-emerald-400" /> 8 horas · acesso imediato</span>
          </div>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div> : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => (
              <div key={c.id} className="flex flex-col rounded-2xl border border-white/10 bg-[#0F213A] p-5 transition-all hover:border-emerald-500/40" data-testid={`pub-course-${c.nr}`}>
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-300">{c.nr}</span>
                  <GraduationCap className="h-5 w-5 text-slate-500" />
                </div>
                <h3 className="mt-3 text-base font-bold">{c.title}</h3>
                <p className="mt-1 line-clamp-3 flex-1 text-xs text-slate-400">{c.summary}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.hours}h</span>
                  <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {c.lessons} aulas</span>
                  <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" /> certificado</span>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-4">
                  <p className="text-lg font-extrabold text-white">{brl(c.price_cents)}</p>
                  <Button size="sm" onClick={() => { setSel(c); setForm({ name: "", email: "", cpf: "" }); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pub-buy-${c.nr}`}>Comprar <ArrowRight className="ml-1 h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-slate-500">
        <p>© {new Date().getFullYear()} Valbrac · Escola Online — Treinamentos SSMA</p>
        <p className="mt-1">Desenvolvido por <span className="font-semibold text-slate-300">Leandro Américo</span></p>
      </footer>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="pub-checkout-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Comprar — {sel?.nr}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{sel?.title} · <b>{sel ? brl(sel.price_cents) : ""}</b></p>
          <div className="space-y-3 py-2">
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Nome completo</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pub-name" /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pub-email" /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">CPF (vai no certificado)</Label><Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} data-testid="pub-cpf" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)}>Cancelar</Button>
            <Button onClick={buy} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pub-pay">{busy ? "Redirecionando..." : "Ir para o pagamento"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
